import { MultiSelectJobApplication } from './types';
import {
    JobApplicationStatus,
    JobApplicationSubStatus,
    jobApplicationStatusLabels,
    jobApplicationSubStatusLabels,
    PremiumJobApplicationStatus,
    PremiumJobApplicationSubStatus,
    premiumJobApplicationStatusLabels,
    premiumJobApplicationSubStatusLabels,
} from './statuses';

// ---------------------------------------------------------------------------
// Effective pipeline resolver
// ---------------------------------------------------------------------------

/**
 * The resolved display pipeline for a job application.
 *
 * Premium applications start in the premium pipeline (managed by Upercut
 * recruiters via `premiumStatus`/`premiumSubStatus`). Once handed off to the
 * client at `DC.Sent` (tracked by `sentToClientAt`/`sentToClientBy`), the
 * effective pipeline switches to the classic pipeline (managed by the client
 * via `status`/`subStatus`).
 */
export interface EffectiveStatus {
    /** The pipeline to render: 'premium' or 'classic'. */
    pipeline: 'premium' | 'classic';
    /** The effective status value for display. */
    status: string | null | undefined;
    /** The effective sub-status value for display. */
    subStatus: string | null | undefined;
    /** True only when a premium app is actively in the premium pipeline (not handed off). */
    isPremiumActive: boolean;
    /** True when a premium app has been handed off to the client (sentToClientAt/By set). */
    isHandedOff: boolean;
}

/**
 * Resolve which status pipeline and values to display for a job application.
 *
 * Premium apps: premium pipeline until handed off → classic pipeline after.
 * Classic/direct_email apps: always classic pipeline.
 */
export const resolveEffectivePipeline = (app: {
    recruitmentMethod?: string | null;
    premiumStatus?: string | null;
    status?: string;
    subStatus?: string;
    premiumSubStatus?: string | null;
    sentToClientAt?: string | null;
    sentToClientBy?: string | null;
}): EffectiveStatus => {
    const isPremium =
        app.recruitmentMethod === 'premium' ||
        (app.recruitmentMethod == null && !!app.premiumStatus);
    const isHandedOff =
        isPremium && !!app.sentToClientAt && !!app.sentToClientBy;

    if (isPremium && !isHandedOff) {
        return {
            pipeline: 'premium',
            status: app.premiumStatus,
            subStatus: app.premiumSubStatus,
            isPremiumActive: true,
            isHandedOff: false,
        };
    }

    return {
        pipeline: 'classic',
        status: app.status,
        subStatus: app.subStatus,
        isPremiumActive: false,
        isHandedOff,
    };
};

/**
 * Returns the shared status if all selected job applications have the same status, otherwise 'mixed'.
 */
export const getMultiSelectJobApplicationStatus = (items: MultiSelectJobApplication[]): string => {
    if (items.length === 0) return 'mixed';
    const first = items[0].status;
    return items.every(item => item.status === first) ? first ?? 'mixed' : 'mixed';
};

const REJECTION_SUB_STATUSES = new Set<string>([
    JobApplicationSubStatus.NewRejected,
    JobApplicationSubStatus.InterviewRejected,
    JobApplicationSubStatus.TrialRejected,
    JobApplicationSubStatus.OfferRejected,
]);

const PREMIUM_REJECTION_SUB_STATUSES = new Set<string>([
    PremiumJobApplicationSubStatus.NewRejected,
    PremiumJobApplicationSubStatus.SystemRejected,
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // PremiumJobApplicationSubStatus.InterviewRejected,
    PremiumJobApplicationSubStatus.TrialRejected,
    // PremiumJobApplicationSubStatus.OfferRejected,
    PremiumJobApplicationSubStatus.Rejected,
    PremiumJobApplicationSubStatus.EmployeeResigned,
    PremiumJobApplicationSubStatus.EmployerTerminated,
]);

// Status color mapping based on Figma design
export const getStatusColors = (status?: string, subStatus?: string) => {
    if (subStatus && REJECTION_SUB_STATUSES.has(subStatus)) {
        return {
            background: '#d34059',
            label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
            subLabel: `(${jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels]})`,
        };
    }
    switch (status) {
        case JobApplicationStatus.New:
            return {
                background: '#4239B0',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'envoyée'
                      })`
                    : '(envoyée)',
            };
        case JobApplicationStatus.Interview:
            return {
                background: '#2f80ed',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'Programmé'
                      })`
                    : '(Programmé)',
            };
        case JobApplicationStatus.Trial:
            return {
                background: '#f2994a',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'Programmé'
                      })`
                    : '(Programmé)',
            };
        case JobApplicationStatus.Offer:
            return {
                background: '#8dd690',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'envoyée'
                      })`
                    : '(envoyée)',
            };
        case JobApplicationStatus.ProbationPeriod:
            return {
                background: '#b08f38',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'sub-stage'
                      })`
                    : '(sub-stage)',
            };
        case JobApplicationStatus.Hired:
            return {
                background: '#118999',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          "Période d'essai"
                      })`
                    : "(Période d'essai)",
            };
        case JobApplicationStatus.Rejected:
            return {
                background: '#d34059',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: subStatus
                    ? `(${
                          jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels] ||
                          'Entretien'
                      })`
                    : '(Entretien)',
            };
        case 'BusinessCase':
            return {
                background: '#bb6bd9',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: '(A programmer)',
            };
        default:
            return {
                background: '#0c0d18',
                label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
                subLabel: '(envoyée)',
            };
    }
};

/** Helper: look up a premium sub-status label within its parent status group. */
const premiumSubStatusLabel = (status?: string, subStatus?: string): string | undefined => {
    if (!status || !subStatus) return undefined;
    const group = premiumJobApplicationSubStatusLabels[status as keyof typeof premiumJobApplicationSubStatusLabels];
    return group?.[subStatus];
};

export const getPremiumStatusColors = (status?: string, subStatus?: string) => {
    const subLabel = premiumSubStatusLabel(status, subStatus);
    if (subStatus && PREMIUM_REJECTION_SUB_STATUSES.has(subStatus)) {
        return {
            background: '#d34059',
            label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
            subLabel: `(${subLabel || subStatus})`,
        };
    }
    switch (status) {
        case PremiumJobApplicationStatus.New:
            return {
                background: '#4239B0',
                label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
                subLabel: subStatus ? `(${subLabel || 'envoyée'})` : '(envoyée)',
            };
        case PremiumJobApplicationStatus.UpercutInterview:
            return {
                background: '#bb6bd9',
                label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
                subLabel: subStatus ? `(${subLabel || 'Programmé'})` : '(Programmé)',
            };
        // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
        // case PremiumJobApplicationStatus.Interview:
        //     return {
        //         background: '#2f80ed',
        //         label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
        //         subLabel: subStatus ? `(${subLabel || 'Programmé'})` : '(Programmé)',
        //     };
        // case PremiumJobApplicationStatus.Questionnaire:
        //     return {
        //         background: '#9b51e0',
        //         label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
        //         subLabel: subStatus ? `(${subLabel || 'À faire'})` : '(À faire)',
        //     };
        // case PremiumJobApplicationStatus.Trial:
        //     return {
        //         background: '#f2994a',
        //         label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
        //         subLabel: subStatus ? `(${subLabel || 'Programmé'})` : '(Programmé)',
        //     };
        // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
        // case PremiumJobApplicationStatus.Offer:
        //     return {
        //         background: '#8dd690',
        //         label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
        //         subLabel: subStatus ? `(${subLabel || 'envoyée'})` : '(envoyée)',
        //     };
        case PremiumJobApplicationStatus.DC:
            return {
                background: '#27ae60',
                label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
                subLabel: subStatus ? `(${subLabel || 'Validé'})` : '(Validé)',
            };
        // case PremiumJobApplicationStatus.DocumentRequest:
        //     return {
        //         background: '#2d9cdb',
        //         label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
        //         subLabel: subStatus ? `(${subLabel || 'À faire'})` : '(À faire)',
        //     };
        // case PremiumJobApplicationStatus.DPAE:
        //     return {
        //         background: '#1abc9c',
        //         label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
        //         subLabel: subStatus ? `(${subLabel || 'À faire'})` : '(À faire)',
        //     };
        // case PremiumJobApplicationStatus.ProbationPeriod:
        //     return {
        //         background: '#b08f38',
        //         label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
        //         subLabel: subStatus ? `(${subLabel || 'sub-stage'})` : '(sub-stage)',
        //     };
        // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
        // case PremiumJobApplicationStatus.Hired:
        //     return {
        //         background: '#118999',
        //         label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
        //         subLabel: subStatus ? `(${subLabel || "Période d'essai"})` : "(Période d'essai)",
        //     };
        case PremiumJobApplicationStatus.Rejected:
            return {
                background: '#d34059',
                label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
                subLabel: subStatus ? `(${subLabel || 'Entretien'})` : '(Entretien)',
            };
        default:
            return {
                background: '#0c0d18',
                label: premiumJobApplicationStatusLabels[status as keyof typeof premiumJobApplicationStatusLabels],
                subLabel: '(envoyée)',
            };
    }
};
